import { Component, Inject, Vue, Watch } from 'vue-property-decorator';
import DirectusService from '@/core/directus/directus.service';
import FAQCMSModel from '@/visa-application/models/FAQCMSModel';
import LanguageCode from '@/locale/language-code';
import _ from 'lodash';
import FaqContentSlotCMSModel from '@/visa-application/models/FaqContentSlotCMSModel';
import FaqCategoryCMSModel from '@/visa-application/models/FaqCategoryCMSModel';
import { Keywords } from '@/visa-application/models/KeywordsCMSModel';
import { GLOBAL_FILTER, MINIO_BUCKET_ADDRESS } from '@/constants';
import { filterDirectusDataByTag, defineCanonicalTag } from '@/shared/utils/utils';

@Component
export default class Faq extends Vue {
  public cmsData: FAQCMSModel = new FAQCMSModel();
  public contentSlotData: FaqContentSlotCMSModel[] = [];
  public contentSlotDataWithoutCategory: FaqContentSlotCMSModel[] = [];
  public contentSlotDataWithCategory: FaqContentSlotCMSModel[] = [];
  public allCategoryAfterDeduplication: FaqCategoryCMSModel[] = [];
  public path = [];
  public searchContent = '';
  public thumpDownGreyPic = MINIO_BUCKET_ADDRESS + '/thump-down-grey.svg';
  public thumpDownBlackPic = MINIO_BUCKET_ADDRESS + '/thump-down-black.svg';
  public thumpUpGreyPic = MINIO_BUCKET_ADDRESS + '/thump-up-grey.svg';
  public thumpUpOrangePic = MINIO_BUCKET_ADDRESS + '/thump-up-orange.svg';
  public keywords: Partial<Keywords> = {};

  @Inject('directusService')
  private directusService: () => DirectusService;
  public residenceCountryCode: string;
  public issuer: string;
  private currentLanguage: LanguageCode = this.$store.getters.currentLanguage;

  public beforeRouteEnter(to, from, next) {
    defineCanonicalTag(to.params.residenceCountryCode, to.params.issuer, to.params.formGroupId, to.fullPath);

    next(async vm => {
      vm.residenceCountryCode = to.params.residenceCountryCode;
      vm.issuer = to.params.issuer;
      vm.getContent(vm.currentLanguage);
      vm.getContentSlot(vm.currentLanguage);
    });
  }

  @Watch('$store.getters.currentLanguage', { deep: true })
  public onLanguageChange(language: LanguageCode) {
    this.currentLanguage = language;
    this.getContent(this.currentLanguage);
    this.getContentSlot(this.currentLanguage);
    this.keywords = this.$store.getters.keywords;
  }

  @Watch('$store.getters.keywords', { immediate: true })
  public onKeywordsChanged(keywords: Keywords): void {
    this.keywords = keywords;
  }

  public async getContent(currentLanguage: LanguageCode): Promise<void> {
    await this.directusService()
      .getCollection<FAQCMSModel>(FAQCMSModel, 'faq', ['*', 'translation.*'], undefined, currentLanguage)
      .then((response: FAQCMSModel[]) => {
        this.cmsData = response[0];
        this.$store.commit('metaInfo', this.cmsData);
        this.path['path'] = window.location.pathname;
        this.$forceUpdate();
      })
      .catch(error => console.error(error));
  }

  public async getContentSlot(currentLanguage: LanguageCode): Promise<void> {
    this.contentSlotData = [];
    const filters = [
      {
        tag: {
          contains: this.residenceCountryCode
        },
        available_from: {
          lte: 'now'
        }
      },
      {
        tag: {
          contains: GLOBAL_FILTER
        },
        available_from: {
          lte: 'now'
        }
      },
      {
        tag: {
          contains: this.issuer
        },
        available_from: {
          lte: 'now'
        }
      },
      {
        tag: {
          contains: this.residenceCountryCode
        },
        available_from: {
          null: ''
        }
      },
      {
        tag: {
          contains: GLOBAL_FILTER
        },
        available_from: {
          null: ''
        }
      },
      {
        tag: {
          contains: this.issuer
        },
        available_from: {
          null: ''
        }
      }
    ];

    const asyncFun = [];
    filters.forEach((filter, index) => {
      const directusRequest = this.directusService()
        .getCollection<FaqContentSlotCMSModel>(FaqContentSlotCMSModel, 'faq_content_slot', ['*.*.*'], filter, currentLanguage)
        .then((response: FaqContentSlotCMSModel[]) => {
          if (response.length > 0) {
            this.contentSlotData = _.sortBy(this.contentSlotData.concat(filterDirectusDataByTag(filter.tag.contains, response)), 'sort');
          }
        });
      asyncFun.push(directusRequest);
    });
    Promise.all(asyncFun).then(() => {
      this.reconstructFaqContentSlotData();
    });
  }

  public async reconstructFaqContentSlotData(): Promise<void> {
    this.contentSlotData.forEach(data => (data.anchor = 'tls-faq-' + data.id));

    this.contentSlotDataWithoutCategory = this.contentSlotData.filter(data => data.faq_category.name.length === 0);

    this.contentSlotDataWithCategory = this.contentSlotData.filter(data => data.faq_category.name.length > 0);

    const allCategoryBeforeDeduplication = this.contentSlotDataWithCategory.map(data => data.faq_category);

    this.allCategoryAfterDeduplication = _.sortBy(
      allCategoryBeforeDeduplication.filter(
        (category, index) => _.findIndex(allCategoryBeforeDeduplication, ['name', category.name]) === index
      ),
      'sort'
    );
  }

  @Watch('searchContent', { immediate: false, deep: false })
  public isSearchEmpty() {
    return (
      this.searchContent.length > 0 &&
      _.filter(this.contentSlotData, FaqContentSlot => {
        return (
          _.get(FaqContentSlot, 'slot_title_translation', '')
            .toLowerCase()
            .indexOf(this.searchContent.trim().toLowerCase()) !== -1 ||
          _.get(FaqContentSlot, 'long_desc', '')
            .toLowerCase()
            .indexOf(this.searchContent.trim().toLowerCase()) !== -1
        );
      }).length === 0
    );
  }

  public isSearchShow(list_item) {
    return (
      list_item.slot_title_translation.toLowerCase().indexOf(this.searchContent.trim().toLowerCase()) !== -1 ||
      list_item.long_desc.toLowerCase().indexOf(this.searchContent.trim().toLowerCase()) !== -1
    );
  }

  public toggleDropdownFaq(index) {
    this.contentSlotData = _.sortBy(this.contentSlotData, 'sort');
    if (this.contentSlotData[index].dropdown_status) {
      this.contentSlotData[index].dropdown_status = !this.contentSlotData[index].dropdown_status;
    } else {
      this.contentSlotData[index].dropdown_status = true;
    }
  }

  public redirectPage(routerUrl: string) {
    const url = this.cmsData[routerUrl];
    if (url.startsWith('http')) {
      window.open(url);
    } else if (url.endsWith(`/${this.residenceCountryCode}/${this.issuer}`)) {
      this.$router.push('/' + url);
    } else {
      this.$router.push(`/${url}/${this.residenceCountryCode}/${this.issuer}`);
    }
  }

  public clickThumpDown(index) {
    this.contentSlotData[index].thump_up = false;
    this.contentSlotData[index].thump_down = !this.contentSlotData[index].thump_down;
  }

  public clickThumpUp(index) {
    this.contentSlotData[index].thump_down = false;
    this.contentSlotData[index].thump_up = !this.contentSlotData[index].thump_up;
  }
}
